// Production-grade error handling utilities

export enum ErrorCode {
  // Authentication errors
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",

  // Validation errors
  INVALID_INPUT = "INVALID_INPUT",
  MESSAGE_TOO_LONG = "MESSAGE_TOO_LONG",
  INVALID_TEMPLATE = "INVALID_TEMPLATE",
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",

  // Business logic errors
  CHAT_RESTRICTED = "CHAT_RESTRICTED",
  TEMPLATE_NOT_ALLOWED = "TEMPLATE_NOT_ALLOWED",
  LEAGUE_MANAGER_ONLY = "LEAGUE_MANAGER_ONLY",
  MATCH_ENDED = "MATCH_ENDED",

  // Event-specific errors
  CLUB_NOT_FOUND = "CLUB_NOT_FOUND",
  EVENT_NOT_FOUND = "EVENT_NOT_FOUND",
  EXTERNAL_ENTITY_NOT_FOUND = "EXTERNAL_ENTITY_NOT_FOUND",
  INSUFFICIENT_CLUB_PERMISSIONS = "INSUFFICIENT_CLUB_PERMISSIONS",
  INSUFFICIENT_LEAGUE_PERMISSIONS = "INSUFFICIENT_LEAGUE_PERMISSIONS",
  ENTITY_NOT_APPROVED = "ENTITY_NOT_APPROVED",
  STRIPE_NOT_CONNECTED = "STRIPE_NOT_CONNECTED",
  INVALID_EVENT_DATES = "INVALID_EVENT_DATES",
  DUPLICATE_EVENT_REQUEST = "DUPLICATE_EVENT_REQUEST",
  REQUEST_ALREADY_PROCESSED = "REQUEST_ALREADY_PROCESSED",
  TICKETING_NOT_ENABLED = "TICKETING_NOT_ENABLED",
  INSUFFICIENT_TICKETS = "INSUFFICIENT_TICKETS",
  TICKET_SALES_ENDED = "TICKET_SALES_ENDED",
  INVALID_QR_CODE = "INVALID_QR_CODE",
  QR_CODE_EXPIRED = "QR_CODE_EXPIRED",

  // System errors
  DATABASE_ERROR = "DATABASE_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
  STRIPE_ERROR = "STRIPE_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export class RealtimeError extends Error {
  public readonly code: ErrorCode;
  public readonly statusCode: number;
  public readonly details?: Record<string, any>;
  public readonly timestamp: Date;
  public readonly userId?: string;

  constructor(
    code: ErrorCode,
    message: string,
    statusCode: number = 400,
    details?: Record<string, any>,
    userId?: string
  ) {
    super(message);
    this.name = "RealtimeError";
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date();
    this.userId = userId;

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, RealtimeError.prototype);
  }

  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      statusCode: this.statusCode,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
      userId: this.userId,
    };
  }
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: RealtimeError[] = [];
  private maxQueueSize = 1000;

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // Handle and log errors
  handleError(
    error: Error | RealtimeError,
    context?: Record<string, any>
  ): RealtimeError {
    let realtimeError: RealtimeError;

    if (error instanceof RealtimeError) {
      realtimeError = error;
    } else {
      // Convert regular errors to RealtimeError
      realtimeError = new RealtimeError(
        ErrorCode.UNKNOWN_ERROR,
        error.message || "An unexpected error occurred",
        500,
        { originalError: error.name, stack: error.stack, context }
      );
    }

    // Log error
    this.logError(realtimeError, context);

    // Add to queue for analytics
    this.addToErrorQueue(realtimeError);

    return realtimeError;
  }

  private logError(error: RealtimeError, context?: Record<string, any>): void {
    const logData = {
      ...error.toJSON(),
      context,
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
    };

    // In production, send to logging service
    if (process.env.NODE_ENV === "production") {
      // Send to configured logging service
      this.sendToLoggingService(logData).catch((err) =>
        console.error("[Logging Service Error]", err)
      );
      console.error("[PRODUCTION ERROR]", JSON.stringify(logData, null, 2));
    } else {
      console.error("[DEV ERROR]", logData);
    }
  }

  private async sendToLoggingService(
    logData: Record<string, any>
  ): Promise<void> {
    // Send errors to Firestore via API endpoint
    // This works from both client and server components
    try {
      // Only log in production, or if explicitly enabled
      if (
        process.env.NODE_ENV !== "production" &&
        !process.env.ENABLE_ERROR_LOGGING
      ) {
        return;
      }

      await fetch("/api/errors/log", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...logData,
          environment: process.env.NODE_ENV,
          userAgent:
            typeof window !== "undefined" ? navigator.userAgent : "server",
          url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });
    } catch (error) {
      // Don't throw - logging failures shouldn't break the app
      console.error("[Error Logging] Failed to send to Firestore:", error);
    }
  }

  private addToErrorQueue(error: RealtimeError): void {
    if (this.errorQueue.length >= this.maxQueueSize) {
      this.errorQueue.shift(); // Remove oldest error
    }
    this.errorQueue.push(error);
  }

  // Get user-friendly error message
  getUserFriendlyMessage(error: RealtimeError): string {
    const messages = {
      [ErrorCode.UNAUTHORIZED]: "Please log in to continue.",
      [ErrorCode.FORBIDDEN]:
        "You don't have permission to perform this action.",
      [ErrorCode.TOKEN_EXPIRED]:
        "Your session has expired. Please log in again.",
      [ErrorCode.INVALID_INPUT]: "Please check your input and try again.",
      [ErrorCode.MESSAGE_TOO_LONG]:
        "Message is too long. Please keep it under 500 characters.",
      [ErrorCode.INVALID_TEMPLATE]: "Selected message template is not valid.",
      [ErrorCode.RATE_LIMIT_EXCEEDED]:
        "You're sending messages too quickly. Please wait a moment.",
      [ErrorCode.CHAT_RESTRICTED]: "This chat only allows predefined messages.",
      [ErrorCode.TEMPLATE_NOT_ALLOWED]:
        "This message template is not allowed in this chat.",
      [ErrorCode.LEAGUE_MANAGER_ONLY]:
        "Only the league manager can modify these settings.",
      [ErrorCode.MATCH_ENDED]: "This match has already ended.",
      [ErrorCode.DATABASE_ERROR]:
        "We're experiencing technical difficulties. Please try again.",
      [ErrorCode.NETWORK_ERROR]:
        "Network connection issue. Please check your connection.",
      [ErrorCode.SERVICE_UNAVAILABLE]:
        "Service is temporarily unavailable. Please try again later.",
      [ErrorCode.UNKNOWN_ERROR]: "Something went wrong. Please try again.",
    };

    return (
      messages[error.code as keyof typeof messages] ||
      "An unexpected error occurred."
    );
  }

  // Get error analytics
  getErrorAnalytics(): {
    totalErrors: number;
    errorsByCode: Record<ErrorCode, number>;
    recentErrors: RealtimeError[];
    errorRate: number; // errors per hour
  } {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    const recentErrors = this.errorQueue.filter(
      (error) => error.timestamp >= oneHourAgo
    );

    const errorsByCode = this.errorQueue.reduce((acc, error) => {
      acc[error.code] = (acc[error.code] || 0) + 1;
      return acc;
    }, {} as Record<ErrorCode, number>);

    return {
      totalErrors: this.errorQueue.length,
      errorsByCode,
      recentErrors: recentErrors.slice(-10), // Last 10 recent errors
      errorRate: recentErrors.length, // Errors in last hour
    };
  }

  // Clear error queue (useful for testing)
  clearErrorQueue(): void {
    this.errorQueue = [];
  }
}

// Validation utilities
export class ValidationUtils {
  static validateMessage(content: string, maxLength: number = 500): void {
    if (!content || typeof content !== "string") {
      throw new RealtimeError(
        ErrorCode.INVALID_INPUT,
        "Message content is required",
        400
      );
    }

    const trimmed = content.trim();
    if (trimmed.length === 0) {
      throw new RealtimeError(
        ErrorCode.INVALID_INPUT,
        "Message cannot be empty",
        400
      );
    }

    if (trimmed.length > maxLength) {
      throw new RealtimeError(
        ErrorCode.MESSAGE_TOO_LONG,
        `Message exceeds maximum length of ${maxLength} characters`,
        400,
        { length: trimmed.length, maxLength }
      );
    }

    // Check for potentially harmful content
    this.validateMessageContent(trimmed);
  }

  private static validateMessageContent(content: string): void {
    // Basic content validation - expand based on your needs
    const prohibitedPatterns = [
      /\b(spam|scam)\b/gi,
      /(https?:\/\/[^\s]+)/gi, // URLs (if you want to restrict them)
      /@everyone|@here/gi, // Mass mentions
    ];

    for (const pattern of prohibitedPatterns) {
      if (pattern.test(content)) {
        throw new RealtimeError(
          ErrorCode.INVALID_INPUT,
          "Message contains prohibited content",
          400,
          { pattern: pattern.source }
        );
      }
    }
  }

  static validateUserId(userId: string): void {
    if (!userId || typeof userId !== "string" || userId.trim().length === 0) {
      throw new RealtimeError(
        ErrorCode.INVALID_INPUT,
        "Valid user ID is required",
        400
      );
    }
  }

  static validateTemplateId(templateId: string): void {
    if (!templateId || typeof templateId !== "string") {
      throw new RealtimeError(
        ErrorCode.INVALID_TEMPLATE,
        "Valid template ID is required",
        400
      );
    }

    // Template ID format validation
    const validTemplateIdPattern =
      /^(greet|schedule|match|general|celebrate|challenge|custom)_\w+$/;
    if (!validTemplateIdPattern.test(templateId)) {
      throw new RealtimeError(
        ErrorCode.INVALID_TEMPLATE,
        "Template ID format is invalid",
        400,
        { templateId }
      );
    }
  }

  static validateChatContext(context: {
    type: string;
    targetId: string;
  }): void {
    const validTypes = ["direct", "league", "club", "match"];

    if (!context.type || !validTypes.includes(context.type)) {
      throw new RealtimeError(
        ErrorCode.INVALID_INPUT,
        "Valid chat context type is required",
        400,
        { provided: context.type, valid: validTypes }
      );
    }

    if (!context.targetId || typeof context.targetId !== "string") {
      throw new RealtimeError(
        ErrorCode.INVALID_INPUT,
        "Valid target ID is required",
        400
      );
    }
  }
}

// Retry mechanism for failed operations
export class RetryHandler {
  static async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000,
    backoffMultiplier: number = 2
  ): Promise<T> {
    let lastError: Error = new Error("Unknown error");

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // Don't retry on certain types of errors
        if (error instanceof RealtimeError) {
          const nonRetryableCodes = [
            ErrorCode.UNAUTHORIZED,
            ErrorCode.FORBIDDEN,
            ErrorCode.INVALID_INPUT,
            ErrorCode.MESSAGE_TOO_LONG,
            ErrorCode.INVALID_TEMPLATE,
          ];

          if (nonRetryableCodes.includes(error.code)) {
            throw error;
          }
        }

        if (attempt === maxRetries) {
          break;
        }

        // Wait before retrying with exponential backoff
        const currentDelay = delay * Math.pow(backoffMultiplier, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
      }
    }

    throw new RealtimeError(
      ErrorCode.SERVICE_UNAVAILABLE,
      `Operation failed after ${maxRetries} attempts: ${lastError.message}`,
      503,
      { originalError: lastError.message, attempts: maxRetries }
    );
  }
}
